import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import api from '../lib/api';
import {
  AnalyticsKpis,
  AnalyticsKpisResponse,
  AnalyticsOverview,
  AnalyticsOverviewResponse,
  AnalyticsRange,
  CourseLearnerMetrics,
  CourseLearnerMetricsResponse,
} from '../types/analytics';

/**
 * Fetch analytics overview data (KPIs)
 * GET /api/v1/analytics/overview
 */
export const fetchAnalyticsOverview = async (): Promise<AnalyticsOverview> => {
  const response = await api.get<AnalyticsOverviewResponse>('/analytics/overview');
  return response.data.data;
};

/**
 * Custom hook to fetch analytics overview data
 * Returns: { data, isLoading, error, refetch }
 */
export const useAnalyticsOverview = (): UseQueryResult<AnalyticsOverview, Error> => {
  return useQuery({
    queryKey: ['analytics', 'overview'],
    queryFn: fetchAnalyticsOverview,
    staleTime: 2 * 60 * 1000, // Consider stale after 2 minutes (KPIs change frequently)
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/** Compounding filters for the aggregate KPIs endpoint. */
export interface KpiFilters {
  courseId?: string;
  userId?: string;
  /**
   * Tier-gated range preset (`?range=...`). Mutually exclusive with a custom
   * `startDate`/`endDate` pair — when both are present, `range` wins and the
   * dates are dropped (the API rejects sending both together).
   */
  range?: AnalyticsRange;
  startDate?: string; // ISO date (YYYY-MM-DD) — custom range (Enterprise only)
  endDate?: string; // ISO date (YYYY-MM-DD) — custom range (Enterprise only)
  compare?: boolean;
}

/**
 * Fetch the filterable KPI payload (completion rate, AHT, feedback, test
 * results) with trends, chart points and optional learner comparison.
 * GET /api/v1/analytics/kpis
 */
export const fetchKpis = async (filters: KpiFilters = {}): Promise<AnalyticsKpis> => {
  const params: Record<string, string> = {};
  if (filters.courseId) params.course_id = filters.courseId;
  if (filters.userId) params.user_id = filters.userId;
  // `range` (a tier preset) and a custom start/end pair are mutually exclusive
  // on the API; prefer the preset and only fall back to explicit dates.
  if (filters.range) {
    params.range = filters.range;
  } else if (filters.startDate && filters.endDate) {
    params.start_date = filters.startDate;
    params.end_date = filters.endDate;
  }
  if (filters.compare) params.compare = 'true';

  const response = await api.get<AnalyticsKpisResponse>('/analytics/kpis', { params });
  return response.data.data;
};

/** Do not retry client errors (4xx) — they will not resolve on retry. */
const retryExceptClientErrors = (failureCount: number, error: Error): boolean => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 1;
};

/**
 * Custom hook to fetch the aggregate KPIs under the given filters.
 */
export const useKpis = (filters: KpiFilters = {}): UseQueryResult<AnalyticsKpis, Error> => {
  return useQuery({
    queryKey: [
      'analytics',
      'kpis',
      filters.courseId ?? 'all',
      filters.userId ?? 'all',
      filters.range ?? null,
      filters.startDate ?? null,
      filters.endDate ?? null,
      Boolean(filters.compare),
    ],
    queryFn: () => fetchKpis(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: retryExceptClientErrors,
    // The dashboard is course-scoped: without a course selection there is
    // nothing meaningful to aggregate, so don't fetch at all.
    enabled: Boolean(filters.courseId),
  });
};

/**
 * Fetch per-learner metric rows for a course (the source rows the frontend
 * turns into a CSV download).
 * GET /api/v1/analytics/courses/:courseId/learner-metrics
 */
export const fetchCourseLearnerMetrics = async (
  courseId: string
): Promise<CourseLearnerMetrics> => {
  const response = await api.get<CourseLearnerMetricsResponse>(
    `/analytics/courses/${courseId}/learner-metrics`
  );
  return response.data.data;
};

/**
 * Custom hook to fetch a course's learner metrics. Disabled until both a
 * courseId and `enabled` are provided so the (potentially large) payload is
 * only fetched on demand.
 */
export const useCourseLearnerMetrics = (
  courseId: string | undefined,
  enabled = true
): UseQueryResult<CourseLearnerMetrics, Error> => {
  return useQuery({
    queryKey: ['analytics', 'course-learner-metrics', courseId ?? 'none'],
    queryFn: () => fetchCourseLearnerMetrics(courseId as string),
    enabled: Boolean(courseId) && enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: retryExceptClientErrors,
  });
};

// ── Learning Program analytics ───────────────────────────────────────────────

export interface ProgramCourseStats {
  learners: number;
  avg_progress: number | null;
  quiz_avg: number | null;
  /** Average survey (feedback) rating on a 5-scale. */
  rating_avg: number | null;
  completion_rate: number | null;
}

export interface ProgramCourseKpiBlock {
  course_id: string;
  title: string;
  /** Stats for this program's learners only. */
  kpis: ProgramCourseStats;
  /** Same stats across everyone who ever took the course. */
  course_average: ProgramCourseStats;
}

export interface ProgramAnalytics {
  program: { id: string; name: string; duration_days: number; status: string };
  kpis: {
    learners_enrolled: number;
    program_completion_rate: number;
    avg_progress: number;
    courses_total: number;
  };
  days: {
    day_id: string;
    day_number: number;
    title: string | null;
    courses: ProgramCourseKpiBlock[];
  }[];
  learners: {
    user_id: string;
    name: string;
    courses_completed: number;
    courses_total: number;
    avg_score: number | null;
    progress_percent: number;
  }[];
}

export const fetchProgramAnalytics = async (
  programId: string
): Promise<ProgramAnalytics> => {
  const res = await api.get<{ data: ProgramAnalytics }>(`/analytics/programs/${programId}`);
  return res.data.data;
};

export const useProgramAnalytics = (programId: string | undefined) =>
  useQuery({
    queryKey: ['analytics', 'program', programId],
    queryFn: () => fetchProgramAnalytics(programId!),
    enabled: !!programId,
  });
